import { ThunkDispatch } from "core-integration/src/store";
import { getEvents } from "core-integration/src/store/events/reducers";
import { updateClassicLeague } from "core-integration/src/store/leagues/thunks";
import {
  IPrivateLeagueAdmin,
  IUpdateClassicLeagueData,
  LeagueCodePrivacy,
} from "core-integration/src/store/leagues/types";
import { Button } from "plos/src/components/buttons/Button";
import { Checkbox, FormSelect, FormTextField } from "plos/src/components/forms";
import Subheading from "plos/src/components/Subheading";
import { useLocalizedNavigate } from "plos/src/hooks/useLocalizedNavigate";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import {
  formElementWrapper,
  formItem,
  leagueGridLayout,
} from "../../../LeaguesAndCups/leaguesAndCups.css";

interface ClassicDetailsProps {
  league: IPrivateLeagueAdmin;
}

interface IState {
  codePrivacy: LeagueCodePrivacy;
  closed: boolean;
  hasCup: boolean | undefined;
  name: string;
  startEvent: number;
}

const initializeState = (league: IPrivateLeagueAdmin): IState => ({
  closed: league.closed,
  codePrivacy: league.code_privacy as LeagueCodePrivacy,
  hasCup: league.has_cup,
  name: league.name,
  startEvent: league.start_event,
});

const ClassicDetails = ({ league }: ClassicDetailsProps) => {
  const { t } = useTranslation("leaguesAndCups");
  const [state, setState] = useState<IState>(initializeState(league));
  useEffect(() => {
    if (league) {
      initializeState(league);
    }
  }, [league]);

  const dispatch = useDispatch<ThunkDispatch>();
  const navigate = useLocalizedNavigate();
  const events = useSelector(getEvents);

  const apiDataFromState = (): IUpdateClassicLeagueData => ({
    admin_entry: league.admin_entry,
    closed: state.closed,
    has_cup: state.hasCup,
    code_privacy: state.codePrivacy,
    name: state.name,
    start_event: state.startEvent,
  });

  const handleNameChange = (val: string) => setState({ ...state, name: val });

  const handleCodePrivacyChange = (e: React.ChangeEvent<HTMLSelectElement>) =>
    setState({ ...state, codePrivacy: e.target.value as LeagueCodePrivacy });

  const handleClosedChange = (val: boolean) =>
    setState({ ...state, closed: val });

  const handleStartEventChange = (e: React.ChangeEvent<HTMLSelectElement>) =>
    setState({ ...state, startEvent: parseInt(e.target.value, 10) });

  const handleSubmit = async (e: React.SyntheticEvent<HTMLFormElement>) => {
    e.preventDefault();
    await dispatch(updateClassicLeague(league.id, apiDataFromState()));
    navigate("/leagues");
  };

  const disabled = !state.name || !state.codePrivacy || !state.startEvent;

  const privacyOptions = {
    a: "All game players",
    l: "League members",
    p: "League administrator",
  };

  return (
    <>
      <Subheading>{t("leagueDetails", "League Details")}</Subheading>
      <form onSubmit={handleSubmit}>
        <div className={leagueGridLayout}>
          <div className={formElementWrapper}>
            <FormTextField
              label={t("leagueName", "League Name")}
              description={t("characterLimit", "Maximum 30 characters")}
              value={state.name}
              onChange={(val) => handleNameChange(val)}
              placeholder={t("enterNameHere", "Enter Name Here")}
              maxLength={30}
              isDisabled={league.name_change_blocked}
            />
            <p>
              {league.name_change_blocked
                ? t(
                    "pendingModeration",
                    "Your requested league name is currently pending moderation and can't be edited at this time.",
                  )
                : t(
                    "appropriateNameGuidance",
                    "Please think carefully before entering your league name. Leagues with names that are deemed inappropriate or offensive may result in your account being deleted. Please refer to the Terms & Conditions of entry for more information.",
                  )}
            </p>
          </div>
          <div className={formItem}>
            <FormSelect
              id="league-code-privacy"
              description={t(
                "leagueCodePrivacy.description",
                "This sets who the league code is viewable by",
              )}
              label={t("leagueCodePrivacy.label", "League code privacy")}
              value={state.codePrivacy}
              onChange={handleCodePrivacyChange}
              required={true}
            >
              {Object.entries(privacyOptions).map(([id, label]) => (
                <option key={id} value={id}>
                  {label}
                </option>
              ))}
            </FormSelect>
          </div>
          <div className={formItem}>
            <FormSelect
              id="scoring-starts"
              description={t(
                "scoringStarts.description",
                "Any changes will be reflected after the next points update",
              )}
              label={t("scoringStarts.label", "Scoring starts")}
              value={state.startEvent}
              onChange={handleStartEventChange}
            >
              {events.map(({ id, name }) => (
                <option key={id} value={`${id}`}>
                  {name}
                </option>
              ))}
            </FormSelect>
          </div>
          <div className={formItem}>
            <Checkbox isSelected={state.closed} onChange={handleClosedChange}>
              {t("closeLeagueToNewEntries", "Close league to new entries")}
            </Checkbox>
          </div>
          <div className={formItem}>
            <Button type="submit" isDisabled={disabled}>
              {t("updateLeague", "Update League")}
            </Button>
          </div>
        </div>
      </form>
    </>
  );
};

export default ClassicDetails;
